import { isVideoFile, isVideoOrAudioFile } from "./file";

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "tiff", "webp"]);

export type CompressionLevel = "low" | "medium" | "high";

type CompressionPreset = {
  quality: number;
  scale?: number;
};

type CompressionOptions = {
  level: CompressionLevel;
};

export const COMPRESSION_PRESETS: Record<CompressionLevel, CompressionPreset> =
  {
    low: { quality: 50, scale: 10 },
    medium: { quality: 50, scale: 30 },
    high: { quality: 50 },
  };

/**
 * 从文件路径中提取文件扩展名
 */
function getFileExtension(filePath: string): string {
  const clean = filePath.split(/[?#]/)[0];
  const dotIndex = clean.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }
  return clean.slice(dotIndex + 1).toLowerCase();
}

/**
 * 判断文件路径是否为图片
 */
function isImageFile(filePath: string): boolean {
  const extension = getFileExtension(filePath);
  return IMAGE_EXTENSIONS.has(extension);
}

/**
 * 构建 OSS 查询参数（用于 static.sandcdn.com）
 */
export function buildOssQuery(preset: CompressionPreset): string {
  const parts = ["image"];

  if (preset.quality) {
    parts.push(`quality,q_${preset.quality}`);
  }

  if (preset.scale) {
    parts.push(`resize,p_${preset.scale}`);
  }

  if (parts.length === 1) {
    return "";
  }

  return parts.join("/");
}

/**
 * 构建图片压缩参数（用于其他 CDN）
 */
export function buildImageParams(preset: CompressionPreset): string[] {
  const params: string[] = [];

  if (preset.quality) {
    params.push(`q${preset.quality}`);
  }

  if (preset.scale) {
    params.push(`x_${preset.scale}`);
  }

  return params;
}

function resolveCompressionPreset(
  options?: CompressionOptions
): CompressionPreset | null {
  if (!options) {
    return null;
  }

  return COMPRESSION_PRESETS[options.level] ?? null;
}

function hasOssCompressionParams(filePath: string): boolean {
  try {
    const urlObj = new URL(filePath);

    return urlObj.searchParams.has("x-oss-process");
  } catch (error) {
    console.error(error);
    return false;
  }
}

function isVidmuseCdnUrl(filePath: string): boolean {
  try {
    const urlObj = new URL(filePath);

    return (
      urlObj.hostname.startsWith("vidmuse") &&
      urlObj.hostname.endsWith(".sandcdn.com") &&
      urlObj.pathname.startsWith("/static/")
    );
  } catch (error) {
    console.error(error);
    return false;
  }
}

function hasVidmuseImageCompression(url: string, preset: CompressionPreset) {
  const imgParams = buildImageParams(preset);
  if (imgParams.length === 0) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const expectedPrefix = `/image/${imgParams.join(",")},jpeg`;

    return path.startsWith(expectedPrefix);
  } catch (error) {
    console.error(error);
    return false;
  }
}

function hasVidmusePreviewCompression(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/").filter(Boolean);
    const threadPart = pathParts.find((part) => part.startsWith("thread_"));

    if (!threadPart) {
      return false;
    }

    const threadIndex = pathParts.indexOf(threadPart);
    const previewIndex = threadIndex + 1;
    const fileIndex = threadIndex + 2;

    return (
      pathParts[previewIndex] === "preview" && Boolean(pathParts[fileIndex])
    );
  } catch {
    return false;
  }
}

function applyVidmusePreviewCompression(url: string): string {
  try {
    const urlObj = new URL(url);
    const origin = urlObj.origin;
    const pathParts = urlObj.pathname.split("/").filter(Boolean);
    const threadPart = pathParts.find((part) => part.startsWith("thread_"));
    const fileName = pathParts[pathParts.length - 1] || "";

    if (!threadPart || !fileName) {
      return url;
    }

    return `${origin}/static/${threadPart}/preview/${fileName}`;
  } catch (error) {
    console.error(error);
    return url;
  }
}

function applyImageCompressionToUrl(
  url: string,
  preset: CompressionPreset
): string {
  const imgParams = buildImageParams(preset);
  if (imgParams.length === 0) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const origin = urlObj.origin;
    const path = urlObj.pathname;

    return `${origin}/image/${imgParams.join(",")},jpeg${path}`;
  } catch (error) {
    console.error(error);
    return url;
  }
}

export function convertFilePathToUrl(
  filePath: string,
  options?: CompressionOptions
) {
  if (!filePath) {
    return "";
  }

  if (typeof filePath !== "string") {
    return "";
  }

  if (filePath.startsWith("blob:")) {
    return filePath;
  }

  // 如果传入的 URL 以 http 开头，先去掉 origin 中的 -internal
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    try {
      const urlObj = new URL(filePath);
      if (urlObj.hostname.includes("-internal")) {
        urlObj.hostname = urlObj.hostname.replace("-internal", "");
        filePath = urlObj.toString();
      }
    } catch {
      // 如果 URL 解析失败，继续使用原始 filePath
    }
  }

  const preset = resolveCompressionPreset(options);
  // 仅对图片应用压缩参数
  const shouldCompress = Boolean(preset) && isImageFile(filePath);

  if (filePath.startsWith("https://static.sandcdn.com")) {
    if (shouldCompress && preset) {
      const ossQuery = buildOssQuery(preset);
      if (ossQuery && !hasOssCompressionParams(filePath)) {
        return `${filePath}?x-oss-process=${ossQuery}`;
      }
    }

    return filePath;
  } else if (
    process.env.NODE_ENV !== "production" &&
    filePath.includes("/thread_") &&
    filePath.startsWith("/") &&
    !filePath.startsWith("/work/") &&
    !filePath.startsWith("/thread_")
  ) {
    // Local absolute paths containing /thread_ (e.g., /Users/aix/code/aion-runtime-dev/thread_xxx/...)
    // Only in development/test environment - extract from thread_ onwards and serve via /static prefix
    const threadMatch = filePath.match(/(thread_[^/]+.*)/);
    if (threadMatch) {
      return `/static/${threadMatch[1]}`;
    }
    return filePath;
  } else if (filePath.startsWith("/work/")) {
    const isVideo = isVideoFile(filePath);
    const isVideoOrAudio = isVideoOrAudioFile(filePath);
    const videoSuffix = isVideoOrAudio ? "-video" : "";

    const url = filePath
      .replace("-internal", "")
      .replace(
        `/work/aion-runtime-dev`,
        `https://vidmuse-dev${videoSuffix}.sandcdn.com/static`
      )
      .replace(
        `/work/aion-runtime-staging`,
        `https://vidmuse-staging${videoSuffix}.sandcdn.com/static`
      )
      .replace(
        `/work/aion-runtime-prod`,
        `https://vidmuse${videoSuffix}.sandcdn.com/static`
      );

    // 如果是视频并且传了 options，且级别不是 high，则使用 preview 路径
    if (isVideo && options && options.level !== "high") {
      try {
        const urlObj = new URL(url);
        const origin = urlObj.origin;
        const pathname = urlObj.pathname;
        const pathParts = pathname.split("/").filter(Boolean);
        const threadPart = pathParts.find((part) => part.startsWith("thread_"));
        const fileName = pathname.split("/").pop() || "";

        if (threadPart && fileName) {
          return `${origin}/static/${threadPart}/preview/${fileName}`;
        }

        return url;
      } catch (e) {
        console.error(e);
        return url;
      }
    }

    if (shouldCompress && preset) {
      const imgParams = buildImageParams(preset);
      if (imgParams.length > 0) {
        try {
          const urlObj = new URL(url);
          const origin = urlObj.origin;
          const path = urlObj.pathname;

          return `${origin}/image/${imgParams.join(",")},jpeg${path}`;
        } catch (e) {
          console.error(e);
          return url;
        }
      }
    }

    return url;
  } else if (isVidmuseCdnUrl(filePath)) {
    if (preset) {
      if (isImageFile(filePath)) {
        if (hasVidmuseImageCompression(filePath, preset)) {
          return filePath;
        }

        return applyImageCompressionToUrl(filePath, preset);
      }

      // Only apply preview compression for video files, not audio files, and only if level is not high
      if (isVideoFile(filePath) && options?.level !== "high") {
        if (hasVidmusePreviewCompression(filePath)) {
          return filePath;
        }

        return applyVidmusePreviewCompression(filePath);
      }

      // For audio files and other non-image, non-video files, return as-is
      return filePath;
    }

    return filePath;
  } else {
    return filePath;
  }
}
